import React from "react";
import { Box, Button, Stack, Text } from "@chakra-ui/react";
import {
  OktonLoadingState,
  OktonLogin,
  OktonSelect,
  useOktonAuth,
  useOktonPlatform,
} from "@okton/platform-ui";
import { AiPlanCatalog } from "./AiPlanCatalog";
import { BranchContext } from "./types";
export const AiPlansPortal: React.FC = () => {
  const { isAuthenticated, isLoading, accessToken, logout } = useOktonAuth();
  const { client } = useOktonPlatform();
  const [branches, setBranches] = React.useState<
    Array<{ companyId: string; id: string; name: string }>
  >([]);
  const [context, setContext] = React.useState<BranchContext | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    if (!isAuthenticated || !accessToken || !client?.getBranches || !client?.getBusinessContext) return;
    let active = true;
    setLoading(true);
    setContext(null);
    setError("");
    Promise.all([client.getBranches(), client.getBusinessContext("ai-plans")])
      .then(([rows, saved]) => {
        if (!active) return;
        const normalizedRows = rows
          .filter((row) => row.companyId != null)
          .map((row) => ({
            companyId: String(row.companyId),
            id: String(row.id),
            name: row.name,
          }));
        setBranches(normalizedRows);
        if (
          saved &&
          normalizedRows.some(
            (row) =>
              row.companyId === String(saved.companyId) &&
              row.id === String(saved.branchId),
          )
        ) {
          setContext({
            ...saved,
            companyId: String(saved.companyId),
            branchId: String(saved.branchId),
          });
        }
      })
      .catch((reason) => {
        if (active)
          setError(
            reason instanceof Error
              ? reason.message
              : "Não foi possível carregar as filiais.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated, accessToken, client]);
  if (isLoading)
    return <OktonLoadingState title="Carregando acesso..." minH="100vh" />;
  if (!isAuthenticated) return <OktonLogin postLoginUrl="/ai-plans" />;
  const selectBranch = async (value: string) => {
    const branch = branches.find(
      (row) => row.companyId + "|" + row.id === value,
    );
    setContext(null);
    if (!branch || !accessToken || !client?.saveBusinessContext) return;
    setLoading(true);
    setError("");
    try {
      const result = await client.saveBusinessContext("ai-plans", {
        companyId: String(branch.companyId),
        branchId: String(branch.id),
        branchName: branch.name,
      });
      setContext({
        ...result,
        companyId: String(result.companyId),
        branchId: String(result.branchId),
      });
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Não foi possível selecionar a filial.",
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box maxW="1200px" mx="auto" p={{ base: 4, md: 8 }}>
      <Stack spacing="5">
        <Stack direction="row" justify="space-between" align="center">
          <Text fontSize="xl" fontWeight="bold">
            Okton · Planos de IA
          </Text>
          <Button variant="ghost" onClick={() => logout()}>
            Sair
          </Button>
        </Stack>
        <OktonSelect
          name="ai-plan-branch"
          label="Filial da contratação"
          aria-label="Filial da contratação"
          isDisabled={loading}
          value={context ? context.companyId + "|" + context.branchId : ""}
          onChange={(event) => selectBranch(event.target.value)}
        >
          <option value="">Selecione a filial</option>
          {branches.map((row) => (
            <option
              key={row.companyId + "|" + row.id}
              value={row.companyId + "|" + row.id}
            >
              {row.name} · Empresa {row.companyId}
            </option>
          ))}
        </OktonSelect>
        {error && (
          <Text role="alert" color="red.500">
            {error}
          </Text>
        )}
        {loading && (
          <OktonLoadingState title="Atualizando filial..." minH="80px" />
        )}
        {context && accessToken && (
          <AiPlanCatalog
            key={context.companyId + "|" + context.branchId}
            token={accessToken}
            system="ai-plans"
            context={context}
          />
        )}
      </Stack>
    </Box>
  );
};
